import {Box, CircularProgress, Drawer, IconButton, Stack, Typography} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../app/store/hooks";
import {setPaymentOpen} from "../app/store/slices/appSlice";
import {PaymentPack} from "../shared/ui/PaymentPack";
import {PaymentProvider} from "../shared/ui/PaymentProvider";
import {useMakePayment} from "../entities/hooks/useMakePayment.ts";
import {useUserInfoUpdate} from "../entities/hooks/useUserInfoUpdate.ts";
import {PaymentProviderEnum} from "../shared/types/PaymentProviderEnum.ts";
import {useEffect, useMemo, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";

export enum PaymentFlow {
    isLoading = 'isLoading',
    isChooseProvider = 'isChooseProvider',
    isChoosePack = 'isChoosePack',
}

export const Payment = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.app.isPaymentOpen);
    const paymentPacks = useAppSelector((state) => state.presets.paymentPacks ?? {});
    const paymentProviders = useAppSelector((state) => state.presets.paymentProviders ?? []);
    const [paymentFlow, setPaymentFlow] = useState<PaymentFlow>(PaymentFlow.isChooseProvider);
    const [selectedProvider, setSelectedProvider] = useState<PaymentProviderEnum | null>(null);
    const {makePayment, isLoading} = useMakePayment();
    const updateUserInfo = useUserInfoUpdate();


    const handleClose = () => {
        dispatch(setPaymentOpen(false));
        setSelectedProvider(null);
    };




    const handleProviderClick = (provider: PaymentProviderEnum) => {
        setSelectedProvider(provider);
        setPaymentFlow(PaymentFlow.isChoosePack);
    };


    useEffect(() => {
        if(isLoading) {
            setPaymentFlow(PaymentFlow.isLoading);
        } else if (!selectedProvider) {
            setPaymentFlow(PaymentFlow.isChooseProvider);
        } else {
            setPaymentFlow(PaymentFlow.isChoosePack);
        }
    }, [isLoading, selectedProvider]);


    const handlePackClick = async (packId: string) => {
        if (!selectedProvider) return;
        await makePayment(packId, selectedProvider);
        await updateUserInfo();
        handleClose();
    };

    const content = useMemo(() => {
        switch (paymentFlow) {
            case PaymentFlow.isLoading:
                return (
                    <Box height={'20dvh'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                        <CircularProgress/>
                    </Box>
                )
            case PaymentFlow.isChooseProvider:
                return (
                    <Stack spacing={1} width={'100%'} maxWidth={'95vw'}>
                        {paymentProviders.map((provider) => (
                            <PaymentProvider
                                key={provider}
                                provider={provider}
                                onClick={() => handleProviderClick(provider)}
                            />
                        ))}
                    </Stack>
                )
            case PaymentFlow.isChoosePack:
                return (
                    <Stack spacing={1} width={'100%'} maxWidth={'95vw'}>
                        {selectedProvider && paymentPacks[selectedProvider]?.map((pack) => (
                            <PaymentPack
                                key={pack.id}
                                pack={pack}
                                paymentProvider={selectedProvider}
                                onClick={() => handlePackClick(pack.id)}
                            />
                        ))}
                    </Stack>
                )
        }
    }, [paymentFlow, selectedProvider, paymentPacks, paymentProviders]);

    const title = useMemo(() => {
        switch (paymentFlow) {
            case PaymentFlow.isLoading:
                return 'Loading...'
            case PaymentFlow.isChooseProvider:
                return 'Choose payment provider'
            case PaymentFlow.isChoosePack:
                return 'Choose payment pack'
        }
    }, [paymentFlow])


    return (
        <Drawer
            anchor="bottom"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    padding: '24px',
                    maxHeight: '85vh',
                }
            }}
        >
            <IconButton onClick={handleClose} sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                WebkitBackdropFilter: 'none',
                backdropFilter: 'none',
                opacity: 0.7,
            }}>
                <CloseIcon/>
            </IconButton>
            <Stack spacing={3} alignItems={'center'}>
                <Typography variant="h5" sx={{fontWeight: '600', textAlign: 'center'}}>
                    {title}
                </Typography>
                {content}
            </Stack>
        </Drawer>
    );
};
